import {createStyles, Stack, Text} from '@mantine/core';
import React, {useEffect, useRef, useState} from 'react';

import notifications from 'notifications';
import {updateScratchPlates} from 'plates/dao/ScratchPlateDAO';
import SelectablePlateList, {SelectablePlate} from 'samples/plate-maps/scratch-plates/SelectablePlateList';
import {PlateSize} from 'samples/storables/StorableSchemaConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

export interface ShowHidePlate extends SelectablePlate {
  isHidden: boolean;
}

export interface ShowHidePlatesModalProps {
  plates: Array<ShowHidePlate>;
  plateSize: PlateSize;
  onRequestClose: () => void;
  onCompleted?: () => void;
}

function ShowHidePlatesModal({
  plates,
  plateSize,
  onRequestClose,
  onCompleted,
}: ShowHidePlatesModalProps): JSX.Element {
  const {classes} = useStyles();
  if (plates.length === 0) {
    throw new Error('ShowHidePlatesModal requires at least one plate.');
  }

  // A plate starts selected when it's currently visible (not hidden); deselecting it hides it.
  const [selectedPlateIds, setSelectedPlateIds] = useState<ReadonlySet<string>>(
    () => new Set(plates.filter((plate) => !plate.isHidden).map((plate) => plate.scratchPlateId))
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A successful submit calls onRequestClose, which unmounts this modal. The submit's
  // `finally` would then setState on an unmounted component, so guard against that.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  function handleToggleSelected(scratchPlateId: string): void {
    setSelectedPlateIds((prev) => {
      const next = new Set(prev);
      if (next.has(scratchPlateId)) {
        next.delete(scratchPlateId);
      } else {
        next.add(scratchPlateId);
      }
      return next;
    });
  }

  function handleSelectAll(): void {
    setSelectedPlateIds(new Set(plates.map((plate) => plate.scratchPlateId)));
  }

  function handleDeselectAll(): void {
    setSelectedPlateIds(new Set());
  }

  async function handleSubmit(): Promise<void> {
    setIsSubmitting(true);
    try {
      await updateScratchPlates(
        plates.map((plate) => ({
          id: plate.scratchPlateId,
          isHidden: !selectedPlateIds.has(plate.scratchPlateId),
        }))
      );
      onCompleted?.();
      onRequestClose();
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.error(error.message);
      } else if (error != null && typeof error === 'object') {
        notifications.notifyError(error as Response);
      } else {
        throw error;
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }

  const buttons: Array<ModalButton> = [
    {text: 'Cancel', cancel: true, style: 'default'},
    {text: isSubmitting ? 'Saving...' : 'Done', submit: true, style: 'primary', disabled: isSubmitting},
  ];

  return (
    <Modal
      title="Show/hide plates"
      large={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={buttons}
    >
      <Stack>
        <Text className={classes.text}>
          Only plate records that are selected below will be displayed. Deselect a plate record to hide it
          from view.
        </Text>
        <SelectablePlateList
          plates={plates}
          plateSize={plateSize}
          selectedPlateIds={selectedPlateIds}
          onToggleSelected={handleToggleSelected}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
        />
      </Stack>
    </Modal>
  );
}

export default ShowHidePlatesModal;

export function openShowHidePlatesModal(props: {
  plates: Array<ShowHidePlate>;
  plateSize: PlateSize;
  onCompleted?: () => void;
}): void {
  const {plates, plateSize, onCompleted} = props;
  if (plates.length === 0) {
    throw new Error('openShowHidePlatesModal requires at least one plate.');
  }
  legacyRunModal(ShowHidePlatesModal, {plates, plateSize, onCompleted});
}

const useStyles = createStyles((theme) => ({
  text: {
    fontSize: theme.fontSizes.md,
  },
}));
